import React, { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../utils/dateUtils';
import { LogIn, LogOut, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Phone, User, MapPin, FileText, Calendar, Package, RefreshCw, Eye, X, Edit3, CheckCircle, AlertCircle, ArrowRight, Trash2, AlertTriangle, MessageSquare, Send, Clock } from 'lucide-react';

// Status flow config
const STATUS_CONFIG = {
  pending:   { label: 'รอดำเนินการ', style: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-400' },
  contacted: { label: 'ติดต่อแล้ว',   style: 'bg-blue-100 text-blue-800 border-blue-300',     dot: 'bg-blue-400' },
  confirmed: { label: 'ยืนยันแล้ว',   style: 'bg-green-100 text-green-800 border-green-300',   dot: 'bg-green-400' },
  completed: { label: 'ปิดการขาย',    style: 'bg-purple-100 text-purple-800 border-purple-300', dot: 'bg-purple-400' },
  cancelled: { label: 'ยกเลิก',       style: 'bg-red-100 text-red-800 border-red-300',         dot: 'bg-red-400' },
};

// Valid next statuses per current status (following: pending → contacted → confirmed → completed, cancelled anytime)
const VALID_TRANSITIONS = {
  pending:   ['contacted', 'cancelled'],
  contacted: ['confirmed', 'cancelled'],
  confirmed: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

const BookingProductComponent = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Data state
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

  // Search/filter
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Detail modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Remarks
  const [remarks, setRemarks] = useState([]);
  const [remarksLoading, setRemarksLoading] = useState(false);
  const [newRemark, setNewRemark] = useState('');
  const [remarkCreatedBy, setRemarkCreatedBy] = useState('');
  const [addingRemark, setAddingRemark] = useState(false);

  // Status update modal
  const [statusModal, setStatusModal] = useState(null); // booking object or null
  const [newStatus, setNewStatus] = useState('');
  const [contactedBy, setContactedBy] = useState('');
  const [remark, setRemark] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState(null); // { success, message }

  // Delete modal
  const [deleteModal, setDeleteModal] = useState(null); // booking object or null
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null); // { success, message }

  // Check stored auth on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem('booking_auth');
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    setTimeout(() => {
      if (username === 'admin' && password === 'admin') {
        localStorage.setItem('booking_auth', 'true');
        setIsAuthenticated(true);
        setLoginError('');
      } else {
        setLoginError('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง');
      }
      setLoginLoading(false);
    }, 500);
  };

  const handleLogout = () => {
    localStorage.removeItem('booking_auth');
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/solarcell-booking?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result.status && result.data) {
        setBookings(result.data.data || []);
        setPagination(result.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 });
      } else {
        throw new Error(result.result_desc || 'Failed to fetch bookings');
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
    }
  }, [isAuthenticated, fetchBookings]);

  // Fetch booking detail by ID (latest data including Remark, ContactedBy)
  const fetchBookingDetail = async (bookingId) => {
    setDetailLoading(true);
    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/solarcell-booking/${bookingId}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.status && result.data) {
        setSelectedBooking(result.data);
      } else {
        throw new Error(result.result_desc || 'Failed to fetch booking detail');
      }
    } catch (err) {
      console.error('Error fetching booking detail:', err);
      // Fallback: use data from list
      const fallback = bookings.find((b) => b.BookingID === bookingId);
      if (fallback) setSelectedBooking(fallback);
    } finally {
      setDetailLoading(false);
    }
  };

  // Fetch remarks for a booking
  const fetchRemarks = async (bookingId) => {
    setRemarksLoading(true);
    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/solarcell-booking/${bookingId}/remarks`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.status) {
        setRemarks(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching remarks:', err);
      setRemarks([]);
    } finally {
      setRemarksLoading(false);
    }
  };

  // Add a new remark
  const handleAddRemark = async () => {
    if (!selectedBooking || !newRemark.trim()) return;

    setAddingRemark(true);
    try {
      const body = { remark: newRemark.trim() };
      if (remarkCreatedBy.trim()) body.createdBy = remarkCreatedBy.trim();

      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/solarcell-booking/${selectedBooking.BookingID}/remarks`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      const result = await response.json();
      if (result.status) {
        setNewRemark('');
        // Refresh remarks
        fetchRemarks(selectedBooking.BookingID);
      }
    } catch (err) {
      console.error('Error adding remark:', err);
    } finally {
      setAddingRemark(false);
    }
  };

  // Open detail modal (fetch latest data + remarks)
  const openDetailModal = (booking) => {
    setSelectedBooking(booking); // show immediately with list data
    setRemarks([]);
    setNewRemark('');
    fetchBookingDetail(booking.BookingID);
    fetchRemarks(booking.BookingID);
  };

  // Open status update modal
  const openStatusModal = (booking) => {
    const transitions = VALID_TRANSITIONS[booking.BookingStatus] || [];
    setStatusModal(booking);
    setNewStatus(transitions[0] || '');
    setContactedBy('');
    setRemark('');
    setUpdateResult(null);
    // Close detail modal if open
    setSelectedBooking(null);
  };

  // Close status update modal
  const closeStatusModal = () => {
    setStatusModal(null);
    setNewStatus('');
    setContactedBy('');
    setRemark('');
    setUpdateResult(null);
  };

  // Open delete modal
  const openDeleteModal = (booking) => {
    setDeleteModal(booking);
    setDeleteConfirmText('');
    setDeleteResult(null);
    setSelectedBooking(null);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModal(null);
    setDeleteConfirmText('');
    setDeleteResult(null);
  };

  // Submit delete
  const handleDelete = async () => {
    if (!deleteModal || deleteConfirmText !== 'admin') return;

    setDeleting(true);
    setDeleteResult(null);

    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/solarcell-booking/${deleteModal.BookingID}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const result = await response.json();

      if (result.status) {
        setDeleteResult({ success: true, message: 'ลบการจองสำเร็จ' });
        setTimeout(() => {
          closeDeleteModal();
          fetchBookings();
        }, 1200);
      } else {
        setDeleteResult({ success: false, message: result.result_desc || 'ลบการจองไม่สำเร็จ' });
      }
    } catch (err) {
      console.error('Error deleting booking:', err);
      setDeleteResult({ success: false, message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  // Submit status update
  const handleUpdateStatus = async () => {
    if (!statusModal || !newStatus) return;

    setUpdating(true);
    setUpdateResult(null);

    try {
      const body = { bookingStatus: newStatus };
      if (contactedBy.trim()) body.contactedBy = contactedBy.trim();
      if (remark.trim()) body.remark = remark.trim();

      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/solarcell-booking/${statusModal.BookingID}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );

      const result = await response.json();

      if (result.status) {
        setUpdateResult({ success: true, message: 'อัพเดทสถานะสำเร็จ' });
        // Refresh data after short delay to show success message
        setTimeout(() => {
          closeStatusModal();
          fetchBookings();
        }, 1200);
      } else {
        setUpdateResult({ success: false, message: result.result_desc || 'อัพเดทสถานะไม่สำเร็จ' });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setUpdateResult({ success: false, message: err.message });
    } finally {
      setUpdating(false);
    }
  };


  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status];
    if (!config) return <span className="px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-800 border-gray-300">{status}</span>;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.style}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
        {config.label}
      </span>
    );
  };

  const filteredBookings = bookings.filter((b) => {
    const matchSearch =
      !searchText ||
      (b.CustomerName || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (b.BookingCode || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (b.Phone || '').includes(searchText);
    const matchStatus = statusFilter === 'all' || b.BookingStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  // Login page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <div className="text-center mb-8">
            <img src="/logo-psi2018.png" alt="PSI Logo" className="w-16 h-16 object-contain mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800">Booking Management</h1>
            <p className="text-gray-500 mt-1">ระบบหลังบ้าน ตรวจสอบการจองโปรโมชั่น PSI Solar Cell</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อผู้ใช้</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Username"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">รหัสผ่าน</label>
              <div className="relative">
                <LogIn className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="Password"
                />
              </div>
            </div>

            {loginError && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm border border-red-200">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading || !username || !password}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loginLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <LogIn className="w-5 h-5" />
              )}
              {loginLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main booking page
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-psi2018.png" alt="PSI Logo" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Booking Management</h1>
              <p className="text-sm text-gray-500">ระบบหลังบ้าน ตรวจสอบการจองโปรโมชั่น PSI Solar Cell</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition"
          >
            <LogOut className="w-4 h-4" />
            ออกจากระบบ
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-sm text-gray-500">ทั้งหมด</p>
            <p className="text-2xl font-bold text-gray-800">{pagination.total}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-sm text-yellow-600">รอดำเนินการ</p>
            <p className="text-2xl font-bold text-yellow-600">
              {bookings.filter((b) => b.BookingStatus === 'pending').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-sm text-blue-600">ติดต่อแล้ว</p>
            <p className="text-2xl font-bold text-blue-600">
              {bookings.filter((b) => b.BookingStatus === 'contacted').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-sm text-green-600">ยืนยันแล้ว</p>
            <p className="text-2xl font-bold text-green-600">
              {bookings.filter((b) => b.BookingStatus === 'confirmed').length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-sm text-purple-600">ปิดการขาย</p>
            <p className="text-2xl font-bold text-purple-600">
              {bookings.filter((b) => b.BookingStatus === 'completed').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="ค้นหาชื่อ, เลขที่จอง, เบอร์โทร..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="all">ทุกสถานะ</option>
              <option value="pending">รอดำเนินการ</option>
              <option value="contacted">ติดต่อแล้ว</option>
              <option value="confirmed">ยืนยันแล้ว</option>
              <option value="completed">ปิดการขาย</option>
              <option value="cancelled">ยกเลิก</option>
            </select>
            <button
              onClick={fetchBookings}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              รีเฟรช
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-500">กำลังโหลดข้อมูล...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                    <th className="px-4 py-3 text-left text-sm font-medium">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">เลขที่จอง</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">ชื่อลูกค้า</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">เบอร์โทร</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">โปรโมชัน</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">สถานะ</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">วันที่จอง</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-10 text-gray-500">
                        ไม่พบข้อมูลการจอง
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((booking, index) => {
                      const transitions = VALID_TRANSITIONS[booking.BookingStatus] || [];
                      return (
                        <tr key={booking.BookingID} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3 text-sm text-gray-500">{booking.RowNum || index + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium text-blue-600">{booking.BookingCode}</td>
                          <td className="px-4 py-3 text-sm text-gray-800">{booking.CustomerName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{booking.Phone || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{booking.PromotionName || '-'}</td>
                          <td className="px-4 py-3 text-center">{getStatusBadge(booking.BookingStatus)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(booking.CreatedDate)}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => openDetailModal(booking)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition text-sm"
                                title="ดูรายละเอียด"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {transitions.length > 0 && (
                                <button
                                  onClick={() => openStatusModal(booking)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-orange-600 hover:bg-orange-50 rounded-lg transition text-sm"
                                  title="อัพเดทสถานะ"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => openDeleteModal(booking)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-red-500 hover:bg-red-50 rounded-lg transition text-sm"
                                title="ลบการจอง"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t bg-gray-50 gap-3">
            <p className="text-sm text-gray-500">
              แสดง {filteredBookings.length > 0 ? ((page - 1) * limit) + 1 : 0}-{Math.min(page * limit, pagination.total)} จาก {pagination.total} รายการ
            </p>
            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title="หน้าแรก"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title="หน้าก่อนหน้า"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {(() => {
                  const totalPages = pagination.totalPages;
                  const pages = [];
                  let startPage = Math.max(1, page - 2);
                  let endPage = Math.min(totalPages, page + 2);

                  if (endPage - startPage < 4) {
                    if (startPage === 1) {
                      endPage = Math.min(totalPages, startPage + 4);
                    } else {
                      startPage = Math.max(1, endPage - 4);
                    }
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button key={1} onClick={() => setPage(1)} className="w-8 h-8 rounded-lg text-sm font-medium hover:bg-gray-200 transition">1</button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="dots-start" className="px-1 text-gray-400">...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                          i === page
                            ? 'bg-blue-500 text-white shadow-sm'
                            : 'hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="dots-end" className="px-1 text-gray-400">...</span>);
                    }
                    pages.push(
                      <button key={totalPages} onClick={() => setPage(totalPages)} className="w-8 h-8 rounded-lg text-sm font-medium hover:bg-gray-200 transition">{totalPages}</button>
                    );
                  }

                  return pages;
                })()}
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title="หน้าถัดไป"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(pagination.totalPages)}
                  disabled={page >= pagination.totalPages}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title="หน้าสุดท้าย"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-bold text-gray-800">รายละเอียดการจอง</h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="p-2 hover:bg-gray-100 rounded-xl transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {detailLoading && (
                <div className="flex items-center justify-center py-2">
                  <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
                  <span className="ml-2 text-xs text-gray-400">กำลังโหลดข้อมูลล่าสุด...</span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-600">{selectedBooking.BookingCode}</span>
                {getStatusBadge(selectedBooking.BookingStatus)}
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-1 gap-3">
                <DetailRow icon={<User className="w-4 h-4" />} label="ชื่อลูกค้า" value={selectedBooking.CustomerName} />
                <DetailRow icon={<Phone className="w-4 h-4" />} label="เบอร์โทร" value={selectedBooking.Phone} />
                <DetailRow icon={<FileText className="w-4 h-4" />} label="Line ID" value={selectedBooking.LineID} />
                <DetailRow icon={<Package className="w-4 h-4" />} label="โปรโมชัน" value={selectedBooking.PromotionName || `ID: ${selectedBooking.PromotionID}`} />
                <DetailRow icon={<MapPin className="w-4 h-4" />} label="ที่อยู่" value={selectedBooking.Address} />
              </div>

              {/* Status Timeline */}
              <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">ไทม์ไลน์สถานะ</h3>
                <div className="space-y-3">
                  {/* Created */}
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 mt-1"></span>
                      <span className="w-px h-full bg-gray-300 min-h-[16px]"></span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-700">จองเข้าระบบ (pending)</p>
                      <p className="text-xs text-gray-500">{formatDate(selectedBooking.CreatedDate)}</p>
                    </div>
                  </div>

                  {/* Contacted */}
                  {(selectedBooking.ContactedDate || selectedBooking.ContactedBy) && (
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-400 mt-1"></span>
                        <span className="w-px h-full bg-gray-300 min-h-[16px]"></span>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-700">เซลล์ติดต่อแล้ว (contacted)</p>
                        {selectedBooking.ContactedDate && (
                          <p className="text-xs text-gray-500">{formatDate(selectedBooking.ContactedDate)}</p>
                        )}
                        {selectedBooking.ContactedBy && (
                          <p className="text-xs text-blue-600">โดย: {selectedBooking.ContactedBy}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Updated */}
                  {selectedBooking.UpdatedDate && selectedBooking.BookingStatus !== 'pending' && (
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <span className={`w-2.5 h-2.5 rounded-full mt-1 ${(STATUS_CONFIG[selectedBooking.BookingStatus] || {}).dot || 'bg-gray-400'}`}></span>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-700">อัพเดทล่าสุด ({STATUS_CONFIG[selectedBooking.BookingStatus]?.label || selectedBooking.BookingStatus})</p>
                        <p className="text-xs text-gray-500">{formatDate(selectedBooking.UpdatedDate)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Remarks Section */}
              <div className="mt-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-yellow-800 flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4" />
                    หมายเหตุ ({remarks.length})
                  </h3>
                  {remarksLoading && <RefreshCw className="w-3 h-3 text-yellow-600 animate-spin" />}
                </div>

                {/* Remarks list */}
                {remarks.length > 0 ? (
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {remarks.map((r) => (
                      <div key={r.RemarkID} className="bg-white rounded-lg p-3 border border-yellow-200">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.Remark}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-400">{formatDate(r.CreatedDate)}</span>
                          {r.CreatedBy && (
                            <span className="text-xs text-blue-500">- {r.CreatedBy}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !remarksLoading ? (
                  <p className="text-xs text-yellow-600 mb-3">ยังไม่มีหมายเหตุ</p>
                ) : null}

                {/* Add remark form */}
                <div className="space-y-2">
                  <input
                    type="text"
                    value={remarkCreatedBy}
                    onChange={(e) => setRemarkCreatedBy(e.target.value)}
                    placeholder="ชื่อผู้บันทึก (ไม่บังคับ)"
                    className="w-full px-3 py-2 text-sm border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none bg-white"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newRemark}
                      onChange={(e) => setNewRemark(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && newRemark.trim()) handleAddRemark(); }}
                      placeholder="เพิ่มหมายเหตุ..."
                      className="flex-1 px-3 py-2 text-sm border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none bg-white"
                    />
                    <button
                      onClick={handleAddRemark}
                      disabled={addingRemark || !newRemark.trim()}
                      className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {addingRemark ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Tax Info */}
              {selectedBooking.IsETax && (
                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">ข้อมูลใบกำกับภาษี</h3>
                  <div className="space-y-2">
                    <DetailRow label="ชื่อผู้เสียภาษี" value={selectedBooking.TaxName} />
                    <DetailRow label="เลขประจำตัวผู้เสียภาษี" value={selectedBooking.TaxID} />
                    <DetailRow label="ที่อยู่ออกใบกำกับภาษี" value={selectedBooking.TaxAddress} />
                  </div>
                </div>
              )}

              {/* Action buttons in detail modal */}
              <div className="flex gap-3 mt-4">
                {(VALID_TRANSITIONS[selectedBooking.BookingStatus] || []).length > 0 && (
                  <button
                    onClick={() => openStatusModal(selectedBooking)}
                    className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-medium hover:from-orange-600 hover:to-orange-700 transition flex items-center justify-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    อัพเดทสถานะ
                  </button>
                )}
                <button
                  onClick={() => openDeleteModal(selectedBooking)}
                  className="py-2.5 px-4 border-2 border-red-300 text-red-600 rounded-xl font-medium hover:bg-red-50 transition flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  ลบ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {statusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">อัพเดทสถานะ</h2>
                <p className="text-sm text-gray-500 mt-0.5">{statusModal.BookingCode} - {statusModal.CustomerName}</p>
              </div>
              <button
                onClick={closeStatusModal}
                className="p-2 hover:bg-gray-100 rounded-xl transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Current status → New status visual */}
              <div className="flex items-center justify-center gap-3 p-4 bg-gray-50 rounded-xl">
                {getStatusBadge(statusModal.BookingStatus)}
                <ArrowRight className="w-4 h-4 text-gray-400" />
                {newStatus ? getStatusBadge(newStatus) : <span className="text-sm text-gray-400">เลือกสถานะ</span>}
              </div>

              {/* Status selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">เปลี่ยนสถานะเป็น</label>
                <div className="grid grid-cols-1 gap-2">
                  {(VALID_TRANSITIONS[statusModal.BookingStatus] || []).map((status) => {
                    const config = STATUS_CONFIG[status];
                    const isSelected = newStatus === status;
                    return (
                      <button
                        key={status}
                        onClick={() => setNewStatus(status)}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <span className={`w-3 h-3 rounded-full ${config.dot}`}></span>
                        <span className="text-sm font-medium text-gray-800">{config.label}</span>
                        {isSelected && <CheckCircle className="w-4 h-4 text-blue-500 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contacted by (show when changing to contacted) */}
              {newStatus === 'contacted' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อพนักงานที่ติดต่อ</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={contactedBy}
                      onChange={(e) => setContactedBy(e.target.value)}
                      placeholder="ระบุชื่อพนักงาน..."
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    />
                  </div>
                </div>
              )}

              {/* Remark */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">หมายเหตุ</label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder={
                    newStatus === 'contacted' ? 'เช่น ลูกค้าสนใจ นัดดูหน้างาน...' :
                    newStatus === 'confirmed' ? 'เช่น ลูกค้ายืนยัน รอนัดวันติดตั้ง...' :
                    newStatus === 'completed' ? 'เช่น ออกใบเสนอราคาเรียบร้อย...' :
                    newStatus === 'cancelled' ? 'เช่น ลูกค้าเปลี่ยนใจ ไม่สนใจแล้ว...' :
                    'ระบุหมายเหตุ...'
                  }
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition resize-none"
                />
              </div>

              {/* Result message */}
              {updateResult && (
                <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
                  updateResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {updateResult.success
                    ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    : <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  }
                  {updateResult.message}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={closeStatusModal}
                  disabled={updating}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition disabled:opacity-50"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={updating || !newStatus || updateResult?.success}
                  className="flex-1 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {updating ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {updating ? 'กำลังอัพเดท...' : 'ยืนยัน'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">ยืนยันการลบ</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{deleteModal.BookingCode}</p>
                </div>
              </div>
              <button
                onClick={closeDeleteModal}
                className="p-2 hover:bg-gray-100 rounded-xl transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Warning */}
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <p className="text-sm text-red-700 font-medium mb-1">ข้อมูลจะถูกลบถาวรและไม่สามารถกู้คืนได้</p>
                <div className="mt-2 space-y-1 text-xs text-red-600">
                  <p>ลูกค้า: <span className="font-medium">{deleteModal.CustomerName}</span></p>
                  <p>เบอร์โทร: <span className="font-medium">{deleteModal.Phone || '-'}</span></p>
                  <p>โปรโมชัน: <span className="font-medium">{deleteModal.PromotionName || '-'}</span></p>
                  <p>สถานะ: <span className="font-medium">{STATUS_CONFIG[deleteModal.BookingStatus]?.label || deleteModal.BookingStatus}</span></p>
                </div>
              </div>

              {/* Confirm text input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  พิมพ์ <span className="font-bold text-red-600">admin</span> เพื่อยืนยันการลบ
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="พิมพ์ admin เพื่อยืนยัน..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition"
                  autoFocus
                />
              </div>

              {/* Result message */}
              {deleteResult && (
                <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
                  deleteResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {deleteResult.success
                    ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    : <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  }
                  {deleteResult.message}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition disabled:opacity-50"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting || deleteConfirmText !== 'admin' || deleteResult?.success}
                  className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-medium hover:from-red-600 hover:to-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {deleting ? 'กำลังลบ...' : 'ลบการจอง'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    {icon && <span className="text-gray-400 mt-0.5">{icon}</span>}
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-800">{value || '-'}</p>
    </div>
  </div>
);

export default BookingProductComponent;
